import mergeWith from 'lodash/mergeWith';
import orderBy from 'lodash/orderBy';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { createSelector } from 'reselect';

import coefficients from '../../../../shared/calculate-coefficients';
import { getDateRangeFromCalendar } from '../../../../shared/calendar';

const isRocket = process.env.CPA_ENV === 'rocket';

const defaultInitialState = {
	columns: {
		date: {
			index: 'date'
		},
		hosts: {
			index: 'hosts',
			direction: 'desc'
		},
		hits: {
			index: 'hits',
			direction: 'desc'
		},
		transitions: {
			index: 'transitions',
			direction: 'desc'
		},
		all_conversions: {
			index: 'all_conversions',
			direction: 'desc'
		},
		europe_conversions: {
			index: 'europe_conversions',
			direction: 'desc'
		},
		latin_conversions: {
			index: 'latin_conversions',
			direction: 'desc'
		},
		asia_conversions: {
			index: 'asia_conversions',
			direction: 'desc'
		},
		africa_conversions: {
			index: 'africa_conversions',
			direction: 'desc'
		},
		accepted_conversions: {
			index: 'accepted_conversions',
			direction: 'desc'
		},
		manual_conversions: {
			index: 'manual_conversions',
			direction: 'desc'
		},
		rejected_approved_conversions: {
			index: 'rejected_approved_conversions',
			direction: 'desc'
		},
		pending_conversions: {
			index: 'pending_conversions',
			direction: 'desc'
		},
		invalid_conversions: {
			index: 'invalid_conversions',
			direction: 'desc'
		},
		total_conversions: {
			index: 'total_conversions',
			direction: 'desc'
		},
		declined_conversions: {
			index: 'declined_conversions',
			direction: 'desc'
		},
		epc: {
			index: 'epc',
			direction: 'desc'
		},
		crs: {
			index: 'crs',
			direction: 'desc'
		},
		crl: {
			index: 'crl',
			direction: 'desc'
		},
		approved: {
			index: 'approved',
			direction: 'desc'
		},
		redeemed: {
			index: 'redeemed',
			direction: 'desc'
		},
		profit_usd: {
			index: 'profit_usd',
			direction: 'desc'
		},
		profit_per_app: {
			index: 'profit_per_app',
			direction: 'desc'
		},
		accepted_income_rub: {
			index: 'accepted_income_rub',
			direction: 'desc'
		},
		accepted_income_usd: {
			index: 'accepted_income_usd',
			direction: 'desc'
		},
		accepted_income_per_app: {
			index: 'accepted_income_per_app',
			direction: 'desc'
		},
		accepted_income_epl: {
			index: 'accepted_income_epl',
			direction: 'desc'
		},
		accepted_income: {
			index: 'accepted_income',
			direction: 'desc'
		},
		pending_income: {
			index: 'pending_income',
			direction: 'desc'
		},
		declined_income: {
			index: 'declined_income',
			direction: 'desc'
		},
		bonus: {
			index: 'bonus',
			direction: 'desc'
		}
	},
	sortByColumn: null,
	groupColumns: {
		date: {
			index: 'date',
			title: 'Date',
			direction: 'desc'
		},
		month: {
			index: 'month',
			title: 'Month',
			direction: 'desc'
		},
		week: {
			index: 'week',
			title: 'Week',
			direction: 'desc'
		},
		hour: {
			index: 'hour',
			title: 'Hour',
			direction: 'desc'
		},
		dow: {
			index: 'dow',
			title: 'Dow',
			direction: 'desc'
		},
		offer: {
			index: 'offer',
			title: 'Offer',
			direction: 'asc',
			relationToFilter: 'offers'
		},
		campaign: {
			index: 'campaign',
			title: 'Campaign',
			direction: 'asc',
			relationToFilter: 'campaigns'
		},
		campaign_type: {
			index: 'campaign_type',
			title: 'Campaign type',
			relationToFilter: 'campaign_types'
		},
		space: {
			index: 'space',
			title: 'Space',
			direction: 'asc',
			relationToFilter: 'spaces'
		},
		landing: {
			index: 'landing',
			title: 'Landing',
			direction: 'asc',
			relationToFilter: 'landings'
		},
		transit: {
			index: 'transit',
			title: 'Transit page',
			direction: 'asc',
			relationToFilter: 'transits'
		},
		landing_domains: {
			index: 'landing_domains',
			title: 'Landing domains',
			direction: 'asc',
			relationToFilter: 'landing_domains'
		},
		transit_domains: {
			index: 'transit_domains',
			title: 'Transit domains',
			direction: 'asc',
			relationToFilter: 'transit_domains'
		},
		...(!isRocket ? {} : {
			category: {
				index: 'category',
				title: 'Category',
				direction: 'asc',
				relationToFilter: 'category'
			}
		}),
		country: {
			index: 'country',
			title: 'Country',
			direction: 'asc',
			relationToFilter: 'countries'
		},
		city: {
			index: 'city',
			title: 'City',
			direction: 'asc',
			relationToFilter: 'cities'
		},
		traffic_type: {
			index: 'traffic_type',
			title: 'Traffic type',
			direction: 'asc',
			relationToFilter: 'traffic_types'
		},
		session: {
			index: 'session',
			title: 'Split sessions',
			direction: 'asc'
		},
		affiliate: {
			index: 'affiliate',
			title: 'Affiliate',
			direction: 'asc',
			relationToFilter: 'affiliates'
		},
		manager: {
			index: 'manager',
			title: 'Manager',
			direction: 'asc',
			relationToFilter: 'managers'
		},
		sid1: {
			index: 'sid1',
			title: 'SID 1',
			direction: 'asc',
			relationToFilter: 'sids1'
		},
		sid2: {
			index: 'sid2',
			title: 'SID 2',
			direction: 'asc',
			relationToFilter: 'sids2'
		},
		sid3: {
			index: 'sid3',
			title: 'SID 3',
			direction: 'asc',
			relationToFilter: 'sids3'
		},
		sid4: {
			index: 'sid4',
			title: 'SID 4',
			direction: 'asc',
			relationToFilter: 'sids4'
		},
		sid5: {
			index: 'sid5',
			title: 'SID 5',
			direction: 'asc',
			relationToFilter: 'sids5'
		},
		sale_model: {
			index: 'sale_model',
			title: 'Sale model',
			direction: 'asc',
			relationToFilter: 'sale_models'
		},
		referer_type: {
			index: 'referer_type',
			title: 'Referer type',
			direction: 'asc',
			relationToFilter: 'referer_types'
		},
		referer_domain: {
			index: 'referer_domain',
			title: 'Referer domain',
			direction: 'asc',
			relationToFilter: 'referer_domains'
		},
		region: {
			index: 'region',
			title: 'Region',
			direction: 'asc',
			relationToFilter: 'regions'
		},
		lead_type: {
			index: 'lead_type',
			title: 'Lead type',
			direction: 'asc',
			relationToFilter: 'lead_types'
		},
		status_changed_at: {
			index: 'status_changed_at',
			title: 'Status changed at',
			direction: 'asc',
			relationToFilter: 'status_changed_at'
		},
		traffic_source: {
			index: 'traffic_source',
			title: 'Traffic sources',
			direction: 'asc',
			relationToFilter: 'traffic_source'
		},
		approval_type: {
			index: 'approval_type',
			title: 'Approval type',
			direction: 'asc',
			relationToFilter: 'approval_type'
		}
	},
	sortByGroup: null
};

const createTable = ({
	namespace,
	stateNodeName
}) => {
	// actions types
	const SORT_BY_COLUMN = `${namespace}/${stateNodeName}table-sort-by-column`;
	const SORT_BY_GROUPS = `${namespace}/${stateNodeName}table-sort-by-groups`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		sortByGroups: groupIndex => ({
			type: SORT_BY_GROUPS,
			payload: groupIndex
		})
	};

	// reducers
	const reducers = {
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns }  = state.table;
			const columnIndex = action.payload;
			const direction = sortByColumn && sortByColumn['index'] === columnIndex ?
				sortByColumn.direction === 'desc' ? 'asc' : 'desc' :
				columns[columnIndex].direction;

			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[SORT_BY_GROUPS]: (state, action) => {
			const { sortByGroup, groupColumns }  = state.table;
			const groupIndex = action.payload;
			const direction = sortByGroup && sortByGroup[groupIndex]  ?
				sortByGroup[groupIndex].direction === 'asc' ? 'desc' : 'asc' :
				groupColumns[groupIndex].direction === 'asc' ? 'desc' : 'asc';

			return {
				...state,
				table: {
					...state.table,
					sortByGroup: {
						...state.table.sortByGroup,
						[groupIndex]: {
							...groupColumns[groupIndex],
							direction
						}
					},
					sortByColumn: null
				}
			};
		}
	};

	// selectors
	const getData = state => state.statistics.data;
	const getQuery = state => state.statistics.query;
	const getTable = state => state.statistics.table;
	const getCurrentLang = state => state.root.currentLang;
	const getCalendar = state => state.statistics.calendar;
	const getLists = state => state.root.lists;
	const getFilters = state => state.statistics.filters.selectedFilters;

	const selectors = {
		getData: createSelector(
			getData,
			getQuery,
			getTable,
			getCurrentLang,
			getCalendar,
			getLists,
			getFilters,
			(data, query, table, currentLang, calendar, lists, filters) => {
				const { groups } = query;
				const { groupColumns, sortByGroup, sortByColumn } = table;
				const rows = data && transformRows({data, query, table, currentLang, calendar, lists});
				const shouldShowGroupHeader = groups.length > 1;
				const groupsInfo = groups.map(group => {
					const entity = groupColumns[group];
					return {
						...entity,
						direction: sortByGroup && sortByGroup[group] ?
							sortByGroup[group].direction : groupColumns[group].direction
					};
				});

				const mapDowToDays = currentLang === 'en'
					? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
					: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

				let queryFilters = {};
				filters && forEach(filters, f => queryFilters[f.name] = map(f.value, v => v.value));
				query.filters = queryFilters;

				const selectedFilters = [];
				filters && forEach(filters, f => {
					if (!isEmpty(f.value)) {
						selectedFilters.push({name: f.name, values: f.value});
					}
				});

				return {
					rows,
					groupsInfo,
					shouldShowGroupHeader,
					currentSortedColumn: sortByColumn,
					mapDowToDays,
					dataForGoalsQuery: {
						currentQuery: {
							...query,
							date_range: getDateRangeFromCalendar(calendar)
						},
						selectedFilters,
						conversionsStatuses: lists['conversion-statuses'],
						groupColumns: groupColumns
					}
				};
			}
		)
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
export const transformRows = (arg) =>  {
	const { data, query, table: { groupColumns, sortByGroup, sortByColumn }, currentLang } = arg;
	const isMultiGroups = query.groups.length > 1;
	const calculated = calculateRows(data, currentLang);
	const sortedByGroupsRows = sortByGroups(calculated, query.groups, groupColumns, sortByGroup);
	const transformedRows = new Map();

	if (!isMultiGroups) {
		transformedRows.set('data', {
			rows: sortColumns(sortedByGroupsRows, sortByColumn)
		});
	} else  {
		sortedByGroupsRows.forEach(row => {
			const key = row[query.groups[0]];
			const rows = transformedRows.has(key) ? transformedRows.get(key).rows : [];

			rows.push(row);

			transformedRows.set(key, {
				rows: sortColumns(rows, sortByColumn)
			});

		});
	}

	// add total row to groups row
	for (let [key, value] of transformedRows) {
		if (value.rows.length) {
			transformedRows.set(key, {
				rows: calculateTotalRow(value.rows)
			});
		}
	}

	return transformedRows;
};

const calculateRows = (rows, adminCurrentLang) => rows.map(row => calculate(row, adminCurrentLang));

const sortByGroups = (
	rows,
	groups,
	groupColumns,
	sortByGroup
) => {
	const isMultiGroups = groups.length > 1;
	const getDirection = group => sortByGroup && sortByGroup[group] ?
		sortByGroup[group].direction : groupColumns[group].direction;
	const expressions = [groups[0]];
	const directions = [getDirection(groups[0])];

	if (isMultiGroups) {
		expressions.push(groups[1]);
		directions.push(getDirection(groups[1]));
	}

	return orderBy(rows, expressions.map((field) => {
		return (row) => {
			const value = row[field];

			switch (field) {
				case 'week':
					return moment(value.split(' - ')[0], 'DD.MM.YY').valueOf();
				default:
					return value;
			}
		};
	}), directions);
};

const sortColumns = (rows, sortByColumn) => {
	if (!rows) return null;
	if (!sortByColumn) return rows;
	return orderBy(rows, row => {
		const value = row[sortByColumn.index];
		switch (sortByColumn.index) {
			case 'date':
				return moment(value).valueOf();
			default:
				return parseFloat(value);
		}
	}, sortByColumn.direction);
};

const calculateTotalRow = rows => {
	if (!rows.length) return [];

	if (rows.length === 1) {
		rows.push({ ...rows[0], isTotal: true });
		return rows;
	}

	const total =  rows.reduce((prevRow, curRow) => mergeWith(
		{...prevRow}, {...curRow},
		(prevValue, curValue) => ( parseInt(prevValue) + parseInt(curValue)))
	);

	rows.push(calculate({
		...total,
		isTotal: true
	}));

	return rows;
};

const calculate = (row, adminCurrentLang) => {

	return Object.assign({}, row, {
		[defaultInitialState.columns.epc.index]: coefficients.epc(row['accepted_income'],  row['hosts']),
		[defaultInitialState.columns.crl.index]: coefficients.crl(row['total_conversions'], row['hosts']),
		[defaultInitialState.columns.crs.index]: coefficients.crs(
			Number(row['accepted_conversions']) + Number(row['manual_conversions']),
			row['hosts']
		),
		[defaultInitialState.columns.approved.index]: coefficients.approve(
			Number(row['accepted_conversions']) + Number(row['manual_conversions']),
			row['total_conversions']
		),
		[defaultInitialState.columns.redeemed.index]: coefficients.redeemed(
			row['redeemed_conversions'], row['accepted_crm_conversions']
		),
		[defaultInitialState.columns.profit_per_app.index]:
			Number(row.generic_conversions) && Number(row.profit_usd) / Number(row.generic_conversions),
		[defaultInitialState.columns.accepted_income_per_app.index]:
			Number(row.accepted_income_usd) / (Number(row.accepted_conversions) + Number(row.manual_conversions)),
		[defaultInitialState.columns.accepted_income_epl.index]:
			Number(row.accepted_income_usd) / Number(row.total_conversions),
		transitionPercent: coefficients.transitionPercent(row['transitions'], row['hits'])
	},
	adminCurrentLang !== 'en' ? {
		dow: coefficients.dow(row['dow'])
	} : {});
};

export default createTable;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/statistics/components/table/general/index.js